/**
 * 分镜 keyframe 候选缩略图网格 + "生成新候选"按钮 + 锁定切换
 * （参考电商复刻视觉语言，sky/slate 配色）
 *
 * Hover 蒙层提供三个独立按钮：放大 / 锁定 / 删除。
 * 放大走专用的 FrameLightbox：支持前后翻图 + 底部功能按钮（锁定 / 删除）。
 */

import { useMemo, useState } from 'react';
import {
  Lock,
  LockOpen,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  ZoomIn,
} from 'lucide-react';
import type { DirectorImageJob } from '../../services/directorApi';
import { FrameLightbox, type FrameLightboxItem } from './FrameLightbox';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

interface Props {
  candidates: DirectorImageJob[];
  lockedJobId: string | null;
  /** 是否禁用锁定操作（视频已激活时禁用） */
  lockDisabled?: boolean;
  onGenerateNew: () => void;
  onLock: (imageJobId: string) => void;
  /** 解锁本镜当前锁定的 keyframe（视频未激活时可用） */
  onUnlock?: () => void;
  onDelete: (imageJobId: string) => void;
}

export function KeyframeCandidatesGrid({
  candidates,
  lockedJobId,
  lockDisabled = false,
  onGenerateNew,
  onLock,
  onUnlock,
  onDelete,
}: Props) {
  const [previewId, setPreviewId] = useState<string | null>(null);

  // 可翻图列表：仅 completed + 有 resultUrl
  const lightboxItems = useMemo<FrameLightboxItem[]>(
    () =>
      candidates
        .filter((c) => c.status === 'completed' && !!c.resultUrl)
        .map((c, idx) => ({
          id: c.id,
          url: c.resultUrl!,
          label: c.id === lockedJobId ? `候选 ${idx + 1} · 已锁定` : `候选 ${idx + 1}`,
        })),
    [candidates, lockedJobId]
  );

  return (
    <div>
      {lockDisabled && (
        <div className="mb-2 flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          视频已激活，请先取消或重激活后再修改 keyframe
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {candidates.map((c) => {
          const isLocked = c.id === lockedJobId;
          const canZoom = c.status === 'completed' && !!c.resultUrl;
          return (
            <div
              key={c.id}
              className={`group relative aspect-square overflow-hidden rounded-2xl bg-slate-50 transition ${
                isLocked
                  ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-white'
                  : 'ring-1 ring-slate-200'
              }`}
            >
              {c.status === 'completed' && c.resultUrl ? (
                <img
                  src={resolveBrowserMediaUrl(c.resultUrl)}
                  alt=""
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                  loading="lazy"
                />
              ) : c.status === 'failed' ? (
                <div className="flex h-full w-full items-center justify-center bg-rose-50 text-xs text-rose-500">
                  失败
                </div>
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-slate-100">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              )}

              {c.status === 'completed' && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-950/45 opacity-0 transition group-hover:opacity-100">
                  {canZoom && (
                    <button
                      type="button"
                      onClick={() => setPreviewId(c.id)}
                      className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-sky-50"
                      title="放大查看"
                    >
                      <ZoomIn className="h-4 w-4 text-slate-700" />
                    </button>
                  )}
                  {!lockDisabled && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isLocked && onUnlock) {
                          onUnlock();
                        } else {
                          onLock(c.id);
                        }
                      }}
                      className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-sky-50"
                      title={isLocked ? '点击解锁' : '锁定为本镜首帧'}
                    >
                      {isLocked ? (
                        <Lock className="h-4 w-4 text-sky-600" />
                      ) : (
                        <LockOpen className="h-4 w-4 text-slate-600" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(c.id)}
                    className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-rose-50"
                    title="删除候选"
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </button>
                </div>
              )}

              {isLocked && (
                <div className="absolute right-1.5 top-1.5 rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-md">
                  已锁定
                </div>
              )}
            </div>
          );
        })}

        {/*
         * 生成新候选
         *
         * 设计约束：按钮的可用性由"资源是否可用"决定，不由"上一次提交是否完成"决定。
         * 用户可连续点击发起多张候选并行生成，候选网格里会立刻出现多个生成中占位卡。
         * 后端 maxKeyframeCandidatesPerUser 兜底防御并发滥用。
         */}
        <button
          type="button"
          onClick={onGenerateNew}
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 bg-white text-slate-400 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-600"
        >
          <Plus className="h-5 w-5" />
          <span className="text-[10px]">生成候选</span>
        </button>
      </div>

      {(() => {
        const currentIndex = previewId
          ? lightboxItems.findIndex((it) => it.id === previewId)
          : -1;
        if (currentIndex < 0) return null;
        return (
          <FrameLightbox
            items={lightboxItems}
            currentIndex={currentIndex}
            onIndexChange={(i) => setPreviewId(lightboxItems[i]?.id ?? null)}
            onClose={() => setPreviewId(null)}
            buildActions={(item) => {
              const isLocked = item.id === lockedJobId;
              return [
                {
                  key: 'lock',
                  icon: isLocked ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <LockOpen className="h-4 w-4" />
                  ),
                  label: isLocked ? '解锁' : '锁定为本镜首帧',
                  onClick: () => {
                    if (isLocked && onUnlock) onUnlock();
                    else onLock(item.id);
                  },
                  variant: isLocked ? 'default' : 'primary',
                  show: !lockDisabled,
                },
                {
                  key: 'delete',
                  icon: <Trash2 className="h-4 w-4" />,
                  label: '删除候选',
                  onClick: () => onDelete(item.id),
                  variant: 'danger',
                },
              ];
            }}
          />
        );
      })()}
    </div>
  );
}
